import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { useQuery } from "react-query";
import {
  getCityMasterData,
  getDistrictMasterData,
  getStateMasterData,
} from "../../../../services/master/master_service";
import { getFilters } from "../../../../services/jobs/jobs";
import { useJobsFilterStore } from "../../../../store/use-jobs-filter-store";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface FilterServicesInterface {
  openModal: boolean;
  setOpenModal: (val: boolean) => void;
}

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FilterJobListing({
  openModal,
  setOpenModal,
}: FilterServicesInterface) {
  const {
    stateId,
    setStateId,
    cityId,
    setCityId,
    districtId,
    setDistrictId,
    jobType,
    setJobType,
    experience,
    setExperience,
    selectedStatus,
    setSelectedStatus,
    categoryId,
    setCategoryId,
    subCategoryId,
    setSubCategoryId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    gender,
    setGender,
    source,
    setSource,
    resetFilters,
  } = useJobsFilterStore();

  // Draft filter state — only committed to the store (and therefore only
  // triggers the job list API call) when "Apply Filters" is clicked.
  const [_stateId, _setStateId] = useState<number | null>(stateId);
  const [_cityId, _setCityId] = useState<number | null>(cityId);
  const [_districtId, _setDistrictId] = useState<number | null>(districtId);
  const [_jobType, _setJobType] = useState<number | null>(jobType);
  const [_experience, _setExperience] = useState<number | null>(experience);
  const [_selectedStatus, _setSelectedStatus] = useState<number | null>(
    selectedStatus
  );
  const [_categoryId, _setCategoryId] = useState<number | null>(categoryId);
  const [_subCategoryId, _setSubCategoryId] = useState<number | null>(
    subCategoryId
  );
  const [_startDate, _setStartDate] = useState<string>(startDate);
  const [_endDate, _setEndDate] = useState<string>(endDate);
  const [_selectedGender, _setSelectedGender] = useState<string>(gender);
  const [_source, _setSource] = useState<"admin" | "app" | "">(source);

  function resetDraftFromStore() {
    _setStateId(stateId);
    _setCityId(cityId);
    _setDistrictId(districtId);
    _setJobType(jobType);
    _setExperience(experience);
    _setSelectedStatus(selectedStatus);
    _setCategoryId(categoryId);
    _setSubCategoryId(subCategoryId);
    _setStartDate(startDate);
    _setEndDate(endDate);
    _setSelectedGender(gender);
    _setSource(source);
  }

  // Fetch filter data
  const { data: filterList } = useQuery(["jobFilters"], async () => {
    let response = await getFilters();
    return response?.data?.data;
  });

  const selectedCategory = useMemo(() => {
    return filterList?.categories?.find((category: any) => category?.id === _categoryId);
  }, [filterList?.categories, _categoryId]);

  const selectedCategorySubcategories = useMemo(() => {
    return selectedCategory?.subcategories || [];
  }, [selectedCategory]);

  const { data: states } = useQuery(["master-Data-List-state"], async () => {
    let response = await getStateMasterData();
    return response?.data?.data?.list;
  });

  const { data: city } = useQuery(["cityList", _stateId], async () => {
    if (_stateId) {
      let response = await getCityMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });
  const { data: district } = useQuery(["districtList", _stateId], async () => {
    if (_stateId) {
      let response = await getDistrictMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  useEffect(() => {
    if (!selectedCategorySubcategories?.length) {
      _setSubCategoryId(null);
      return;
    }

    const isValidSubCategory = selectedCategorySubcategories?.some(
      (subcategory: any) => subcategory?.id === _subCategoryId
    );

    if (!isValidSubCategory) {
      _setSubCategoryId(null);
    }
  }, [selectedCategorySubcategories, _subCategoryId]);

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[] | undefined;
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) {
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
          {title}
        </Typography>
        <Autocomplete
          fullWidth
          size="small"
          value={
            (Array.isArray(options)
              ? options.find((option) => selectedId === option?.id)
              : undefined) || null
          }
          onChange={(e, value) => {
            onSelect(value?.id || null);
          }}
          options={Array.isArray(options) ? options : []}
          getOptionLabel={(option) => option?.value || ""}
          renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
        />
      </Box>
    );
  }

  function _chipGroup({
    title,
    options,
    selectedValue,
    onSelect,
  }: {
    title: string;
    options: { id?: number; value: string }[] | undefined;
    selectedValue: string | number | null;
    onSelect: (value: any) => void;
  }) {
    if (!options?.length) return null;
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
          {title}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {options.map((option) => {
            const optionValue = option.id ?? option.value;
            const selected = selectedValue === optionValue;
            return (
              <Chip
                key={option.value}
                label={option.value}
                clickable
                variant={selected ? "filled" : "outlined"}
                color="primary"
                onClick={() => onSelect(selected ? null : optionValue)}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  const handleClose = () => {
    resetDraftFromStore();
    setOpenModal(false);
  };

  return (
    <Dialog open={openModal} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Jobs
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={() => {
                _setStateId(null);
                _setCityId(null);
                _setDistrictId(null);
                _setJobType(null);
                _setExperience(null);
                _setSelectedStatus(null);
                _setCategoryId(null);
                _setSubCategoryId(null);
                _setStartDate("");
                _setEndDate("");
                _setSelectedGender("");
                _setSource("");
                setOpenModal(false);
                resetFilters();
              }}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          {_chipGroup({
            title: "Job Type",
            options: filterList?.jobType || [],
            selectedValue: _jobType,
            onSelect: (id) => _setJobType(id),
          })}
          {_chipGroup({
            title: "Experience Required",
            options: filterList?.workExperience || [],
            selectedValue: _experience,
            onSelect: (id) => _setExperience(id),
          })}
          {_dropdownOption({
            title: "State",
            placeholder: "Search for State",
            options: states || [],
            selectedId: _stateId,
            onSelect: (id) => {
              _setStateId(id);
              _setCityId(null);
              _setDistrictId(null);
            },
          })}
          {_dropdownOption({
            title: "District",
            placeholder: "Search for District",
            options: district,
            selectedId: _districtId,
            onSelect: (id) => {
              _setDistrictId(id);
            },
          })}
          {_dropdownOption({
            title: "City",
            placeholder: "Search for City",
            options: city,
            selectedId: _cityId,
            onSelect: (id) => {
              _setCityId(id);
            },
          })}
          {_dropdownOption({
            title: "Category",
            placeholder: "Search for Category",
            options: filterList?.categories || [],
            selectedId: _categoryId,
            onSelect: (id) => {
              _setCategoryId(id);
              _setSubCategoryId(null);
            },
          })}
          {selectedCategorySubcategories?.length > 0 &&
            _dropdownOption({
              title: "Sub Category",
              placeholder: "Search for Sub Category",
              options: selectedCategorySubcategories,
              selectedId: _subCategoryId,
              onSelect: (id) => {
                _setSubCategoryId(id);
              },
            })}

          <Typography variant="body2" fontWeight={600} color={black} mb={1}>
            Date Range
          </Typography>
          <Box display="flex" gap={1.5} mb={2}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_startDate ? dayjs(_startDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setStartDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              minDate={_startDate ? dayjs(_startDate, "YYYY-MM-DD") : undefined}
              value={_endDate ? dayjs(_endDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setEndDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          {_chipGroup({
            title: "Gender",
            options: filterList?.gender || [],
            selectedValue: _selectedGender,
            onSelect: (value) => _setSelectedGender(value || ""),
          })}
          {_chipGroup({
            title: "Status",
            options: filterList?.status || [],
            selectedValue: _selectedStatus,
            onSelect: (id) => _setSelectedStatus(id),
          })}
          {_chipGroup({
            title: "Created From",
            options: ([
              { id: "admin", value: "Admin" },
              { id: "app", value: "App" },
            ] as unknown) as { id?: number; value: string }[],
            selectedValue: _source,
            onSelect: (value) => _setSource(value || ""),
          })}

          <Box display="flex" gap={1.5} mt={2}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                setStateId(_stateId);
                setCityId(_cityId);
                setDistrictId(_districtId);
                setJobType(_jobType);
                setExperience(_experience);
                setSelectedStatus(_selectedStatus);
                setCategoryId(_categoryId);
                setSubCategoryId(_subCategoryId);
                setStartDate(_startDate);
                setEndDate(_endDate);
                setGender(_selectedGender);
                setSource(_source);
                setOpenModal(false);
              }}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
